import { useMemo } from 'react';
import { CHAINFLIP_BLOCK_TIME_SECONDS } from '@chainflip/utils/consts';
import { isNullish } from '@chainflip/utils/guard';
import { subDays } from 'date-fns';
import { secondsInDay } from 'date-fns/constants';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getOperatorInfoQuery, getValidatorsByIds } from '@/shared/graphql/cache/delegation';
import {
  getAuthorshipData,
  getValidatorsAggregatedStatsQuery,
  getValidatorsTotalRewardsQuery,
} from '@/shared/graphql/explorer/queries';
import { type CacheValidatorFragment } from '@/shared/graphql/generated/cache/graphql';
import { type AuthorshipDataQuery } from '@/shared/graphql/generated/explorer/graphql';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import TokenAmount from '@/shared/utils/TokenAmount';

const REPUTATION_WINDOW_SIZE_DAYS = 3;

export const getOperatorReputation = (
  validators: Pick<OperatorValidator, 'isAuthority' | 'reputation'>[],
  authorshipData: AuthorshipDataQuery | undefined,
): 'N/A' | number | undefined => {
  const authorities = validators.filter((v) => v.isAuthority);

  if (authorshipData?.lastBlock === undefined) return undefined;

  const validatorReputation =
    (authorities.length === 0
      ? 1
      : authorities.reduce((acc, v) => acc + v.reputation, 0) / (authorities.length * 2880)) * 0.1;

  const blocksPerDay = secondsInDay / CHAINFLIP_BLOCK_TIME_SECONDS;
  const lastBlock = authorshipData.lastBlock!.nodes[0].id;
  const absoluteStartBlock = lastBlock - blocksPerDay * REPUTATION_WINDOW_SIZE_DAYS;

  const expectedAuthoredBlocks = authorshipData.operatorEpochs!.nodes.reduce(
    (acc, { epoch, validators: epochValidators }) => {
      // epoch ends before our window, skip
      if (epoch.endBlockId && epoch.endBlockId < absoluteStartBlock) return acc;

      const start = Math.max(epoch.startBlockId, absoluteStartBlock);
      const end = Math.max(epoch.endBlockId ?? 0, lastBlock);
      const blockCount = end - start;
      const blocksPerAuthority = Math.ceil(blockCount / epoch.memberships.totalCount);
      return (
        acc +
        blocksPerAuthority * epochValidators.totalCount +
        (isNullish(epoch.endBlockId) ? 1 : 0)
      );
    },
    0,
  );

  if (expectedAuthoredBlocks === 0) return 'N/A';

  const authorshipRate =
    (Math.max(expectedAuthoredBlocks - authorshipData.authorshipPenalties!.totalCount, 0) /
      expectedAuthoredBlocks) *
    0.9;

  return Math.min(Math.round((authorshipRate + validatorReputation) * 100), 100);
};

export type OperatorValidator = {
  idSs58: `cF${string}`;
  alias: string | undefined;
  balance: TokenAmount;
  totalRewards: TokenAmount;
  distanceFromMAB: TokenAmount;
  redeemable: TokenAmount;
  reputation: number;
  cacheValidator: CacheValidatorFragment;
  cfeVersion: string | undefined;
  isAuthority: boolean;
};

export default function useOperator(
  operatorIdSs58: string,
  connectedAccount?: {
    validators: Map<`cF${string}`, bigint>;
    delegationFeePips: number;
    delegators: Map<`cF${string}`, bigint>;
    exceptionList: `cF${string}`[];
    alias: string | undefined;
    openDelegationEnabled: boolean;
  },
) {
  const { isLoading: operatorLoading, data: { operator, auction } = {} } = useGqlQuery(
    getOperatorInfoQuery,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      variables: {
        idSs58: operatorIdSs58,
      },
      context: { clientName: 'statechainCache' },
      enabled: connectedAccount === undefined,
    },
  );

  const { isLoading: validatorsByIdsLoading, data: validatorsByIds } = useGqlQuery(
    getValidatorsByIds,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      variables: {
        // these accounts will update faster than the cache
        // we want the latest validators to show up quickly
        ids: connectedAccount ? connectedAccount.validators.keys().toArray() : [],
      },
      context: { clientName: 'statechainCache' },
      enabled: connectedAccount !== undefined,
    },
  );

  const validators =
    (operator
      ? operator.validators.nodes.map((n) => n.validator)
      : validatorsByIds?.validators?.nodes) ?? [];

  const { isLoading: isLoadingProcessor, data: processorData } = useGqlQuery(
    getValidatorsTotalRewardsQuery,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      variables: {
        validators: validators.map((v) => v.idSs58),
      },
      enabled: validators.length > 0,
      context: { clientName: 'processor' },
    },
  );

  const { isLoading: isLoadingStats, data: validatorStats } = useGqlQuery(
    getValidatorsAggregatedStatsQuery,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      variables: {
        validators: validators?.map((v) => v.idSs58) ?? [],
      },
      enabled: validators.length > 0,
      context: { clientName: 'processor' },
    },
  );

  const startTime = useMemo(
    () => subDays(Date.now(), REPUTATION_WINDOW_SIZE_DAYS).toISOString(),
    [],
  );
  const { data: authoredBlocksData } = useGqlQuery(getAuthorshipData, {
    refetchInterval: TABLE_POLL_INTERVAL,
    variables: {
      validatorIds: validators?.map((v) => v.idSs58) ?? [],
      startTime,
      operatorId: operatorIdSs58,
    },
    enabled: validators.length > 0,
    context: { clientName: 'processor' },
  });

  return useMemo(() => {
    const formattedValidators =
      validators?.map<OperatorValidator>((validator) => {
        const processorValidator = processorData?.accounts?.nodes.find(
          (v) => v.idSs58 === validator.idSs58,
        );
        const totalBalance = TokenAmount.fromAsset(validator.unlockedBalance, 'Flip').add(
          TokenAmount.fromAsset(validator.lockedBalance, 'Flip'),
        );
        const distanceFromMab = auction?.minActiveBid
          ? totalBalance.sub(auction.minActiveBid).abs()
          : TokenAmount.fromAsset(0, 'Flip');

        return {
          idSs58: validator.idSs58 as `cF${string}`,
          alias: validator.alias ?? undefined,
          balance: totalBalance,
          totalRewards: TokenAmount.fromAsset(
            processorValidator?.validator?.authority.aggregates?.sum?.reward ?? 0,
            'Flip',
          ),
          distanceFromMAB: distanceFromMab,
          redeemable: TokenAmount.fromAsset(validator.unlockedBalance, 'Flip'),
          reputation: validator.reputationPoints,
          cacheValidator: validator,
          cfeVersion: processorValidator?.validator?.cfeVersionId ?? undefined,
          isAuthority: validator.isCurrentAuthority,
        };
      }) ?? [];

    return {
      isLoading: validatorsByIdsLoading || operatorLoading || isLoadingProcessor || isLoadingStats,
      alias: connectedAccount?.alias ?? operator?.account?.alias ?? undefined,
      delegators: new Map(
        operator?.delegators.nodes.map(
          ({ idSs58, upcomingAmount }) =>
            [idSs58, TokenAmount.fromAsset(upcomingAmount, 'Flip')] as [`cF${string}`, TokenAmount],
        ) ??
          new Map(
            connectedAccount?.delegators
              .entries()
              .map(([id, amount]) => [id, TokenAmount.fromAsset(amount, 'Flip')]),
          ),
      ),
      openDelegationEnabled:
        connectedAccount?.openDelegationEnabled ?? operator?.openDelegationEnabled ?? true,
      exceptionList: (connectedAccount?.exceptionList ??
        operator?.exceptionList ??
        []) as `cF${string}`[],
      delegationFeePips: connectedAccount?.delegationFeePips ?? operator?.upcomingDelegationFeeBps,
      totalSlashed: TokenAmount.fromAsset(
        validatorStats?.totalSlashed?.aggregates?.sum?.amount ?? 0,
        'Flip',
      ),
      totalRewards: {
        amount: TokenAmount.fromAsset(
          validatorStats?.totalRewards?.aggregates?.sum?.reward ?? 0,
          'Flip',
        ),
        valueUsd: validatorStats?.totalRewards?.aggregates?.sum?.rewardValueUsd ?? 0,
      },
      reputationScore: getOperatorReputation(formattedValidators, authoredBlocksData),
      validators: formattedValidators,
    };
  }, [
    validators,
    processorData,
    auction,
    operator,
    validatorStats,
    validatorsByIdsLoading,
    authoredBlocksData,
    operatorLoading,
    isLoadingProcessor,
    isLoadingStats,
    connectedAccount,
  ]);
}
